import React, { useEffect, useRef, useState } from 'react';

import { Button, Group, Modal, Stack, Title } from '@mantine/core';
import { RichTextEditor, Link } from '@mantine/tiptap';
import { TextAlign } from '@tiptap/extension-text-align';
import { Underline } from '@tiptap/extension-underline';
import { AnyExtension, useEditor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { useAuth } from 'context/auth-context';
import useBanners from 'hooks/banner/useBanners';
import useCloseBanner from 'hooks/banner/useCloseBanner';
import { BannerEventProperties, useAthleteTracking } from 'hooks/mixpanel/useAthleteTracking';

/**
 * Shows the oldest live banner the signed-in profile was sent, until they dismiss it for good.
 *
 * Nothing opens when the banners cannot be fetched. Viewed, dismissed and link-clicked events fire
 * for an athlete's own session only, never for staff or while impersonating, and carry the banner's
 * id and queue position but never its title or body.
 */
const BannerModal = ({}): JSX.Element => {
  const [isBannerModalOpen, setIsBannerModalOpen] = useState(false);
  const closeBannerMutation = useCloseBanner();
  const { session } = useAuth();
  const { trackBannerViewed, trackBannerDismissed, trackBannerLinkClicked } = useAthleteTracking();
  const viewedBannerIds = useRef(new Set<string>());
  const isTrackedAthlete = session?.role === 'athlete' && !session.isImpersonating;

  const {
    data: bannerData,
    isLoading: bannerLoading,
    isError: bannerError,
    isFetching: bannerFetching,
  } = useBanners();
  const banners = bannerData?.data ?? [];
  const banner = banners[0];

  const editor = useEditor({
    extensions: [
      StarterKit as AnyExtension,
      Underline,
      Link,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
    ],
    content: '',
    editable: false,
  });

  const queueEventProperties = (bannerId: string): BannerEventProperties => ({
    banner_id: bannerId,
    queue_position: 1,
    queue_size: banners.length,
  });

  const handleCloseBanner = (bannerId: string): void => {
    const eventProperties = queueEventProperties(bannerId);
    closeBannerMutation.mutate(bannerId, {
      onSuccess: () => {
        if (isTrackedAthlete) trackBannerDismissed(eventProperties);
      },
      onSettled: () => setIsBannerModalOpen(false),
    });
  };

  const handleContentClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (!isTrackedAthlete || !banner || !(event.target as HTMLElement).closest('a')) return;
    trackBannerLinkClicked(queueEventProperties(banner.bannerId));
  };

  useEffect(() => {
    if (!bannerLoading && !bannerFetching && !bannerError && banner) {
      editor?.commands.setContent(banner.message || '');
      setIsBannerModalOpen(true);
      if (isTrackedAthlete && !viewedBannerIds.current.has(banner.bannerId)) {
        viewedBannerIds.current.add(banner.bannerId);
        trackBannerViewed(queueEventProperties(banner.bannerId));
      }
    } else {
      setIsBannerModalOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, bannerData, bannerLoading, bannerFetching, bannerError]);

  return (
    <>
      <Modal
        opened={isBannerModalOpen}
        onClose={() => setIsBannerModalOpen(true)}
        title={<Title order={3}>{banner?.title || ''}</Title>}
        withCloseButton={false}
      >
        <Stack>
          <RichTextEditor editor={editor}>
            <RichTextEditor.Content onClick={handleContentClick} />
          </RichTextEditor>
        </Stack>
        <Group position='right' sx={{ paddingTop: '10px' }}>
          <Button
            size='sm'
            loading={closeBannerMutation.isLoading}
            onClick={() => handleCloseBanner(banner?.bannerId || '')}
          >
            Do Not Show Again
          </Button>
        </Group>
      </Modal>
    </>
  );
};

export default BannerModal;
